import { splitPartKey, type DraftPart } from "@/lib/assetContent";

/**
 * The typed reading of a shared BG interview profile, and the SURGICAL edits an
 * authoring surface makes to its tone document (#914).
 *
 * ## WHAT A PROFILE IS, AND WHY IT HAS THREE NAMED PARTS
 *
 * #858 published the family: one asset carries a variant's `tone` (structured),
 * its `personality` prompt and its `conversation_prompt`, and the pinned engine
 * reads all three on every BG turn of that variant. `library.py`'s part
 * manifest makes all three REQUIRED, because
 * `filesystem_provider._validate_bg_variant` refuses to build a provider for a
 * variant missing any of them — the whole deployment serves nothing, not just
 * this profile. So a surface over this family shows three named documents, and
 * two of them are ordinary prompts the existing named editor already handles.
 *
 * The third is not. `tone` is a typed pack document whose five keys the engine
 * re-checks at EVERY load (`load_variant_tone_config`), and this module is the
 * projection an authoring form needs to render it: which clauses exist, which
 * shape the transition template is stored in, and the banned-phrase list in its
 * stored order.
 *
 * ## THE ONE RULE THIS MODULE EXISTS FOR, AND IT IS `backgroundDomains`'
 *
 * Every function below reads the stored JSON buffer, replaces exactly the one
 * value that changed IN PLACE, and re-serialises the SAME object. Nothing is
 * rebuilt from the rendered fields. `tone`'s schema is closed
 * (`extra="forbid"`), so unlike a domain document there is no unshown clinical
 * material to lose — but the two shapes `domain_change_template` may take are
 * real, and a form that rebuilt the document would silently rewrite a variant's
 * single shared string into three clauses the author never typed.
 *
 * `JSON.parse` preserves insertion order for string keys and in-place mutation
 * does not disturb it, so a save that changes one clause re-emits every other
 * key in the order it was stored.
 *
 * ## WHY THE SCHEMA IS NOT RESTATED HERE
 *
 * `services/editor-service/src/editor_service/background_profile.py` is the
 * authority: it refuses an unsupported field, a missing required key, a
 * transition template that is neither shape, and an ENABLED session
 * introduction whose ask is blank — each with a field-addressed pointer. A
 * second copy of those rules in the browser would be free to drift from the one
 * that actually refuses, which is the class of duplication #300 spent a ticket
 * deleting.
 *
 * TWO SERVER-OWNED KEYS ARE DELIBERATELY ABSENT from every reading and every
 * write below: `personality_file` and `conversation_prompt_file`. The service
 * REFUSES them as authored input and injects them at emission from the same
 * placement table that decides where those two files land (S7 contract §4 —
 * "internally generated references to the profile's named prompt parts"). A
 * form field for either would be the storage-path editor the ticket's bounded
 * scope forbids, and a value typed into one could not be published.
 *
 * ## EMPTY IS A DECISION HERE, NOT AN OMISSION
 *
 * The source MOD profile ships `session_intro.greeting_template: ""` and
 * `ask: ""` beside `enabled: false`, and the synthetic golden pack ships
 * `bg_intro: ""`. The engine reads an empty tone string as "no clause". So a
 * blank field on this surface writes `""` and preserves it; it never deletes
 * the key, because a missing key is what `load_variant_tone_config` refuses.
 */

/** The three part roles a `background_profile` places, per `placement.py`. */
export const BG_TONE_ROLE = "tone";
export const BG_PERSONALITY_ROLE = "personality";
export const BG_CONVERSATION_PROMPT_ROLE = "conversation_prompt";

/**
 * The language the pinned engine reads a variant's STRUCTURE from, mirroring
 * `placement.BG_STRUCTURE_LANGUAGE`.
 *
 * Used only to pick which document a surface opens FIRST — never to refuse a
 * write. Which languages a profile must carry is the project's own
 * `supported_languages`, answered by adoption preflight.
 */
export const BG_STRUCTURE_LANGUAGE = "he";

/** The three transition clauses `tracker._render_domain_change` selects between. */
export const TRANSITION_KINDS = ["opening", "bare", "next_topic"] as const;
export type TransitionKind = (typeof TRANSITION_KINDS)[number];

/**
 * How a profile stores its domain-change template.
 *
 * Both are shapes the pinned tracker reads: `clauses` is the mapping of three
 * named transitions, and `single` is the one string a variant collapses all
 * three kinds into — what the S7 contract §4 calls "the valid inherited string
 * template shape", which must be preserved rather than normalised away.
 */
export type TransitionShape = "clauses" | "single";

export interface SessionIntroView {
  /** Whether the runtime composes a standalone SELF_INTRO turn at all. */
  enabled: boolean;
  greetingTemplate: string;
  ask: string;
}

export interface ToneView {
  sessionIntro: SessionIntroView;
  /** `bg_intro` — prepended to the turn-1 directive by the tracker. */
  bgIntro: string;
  shape: TransitionShape;
  /** The three clauses, all `""` when the document stores the single shape. */
  clauses: Record<TransitionKind, string>;
  /** The shared string, `""` when the document stores the mapping shape. */
  single: string;
  bannedPhrases: string[];
}

/** One stored tone document, with the part it came from. */
export interface ToneDocument {
  /** The `DraftPart.id` this document is stored in. */
  documentId: string;
  language: string | null;
  /** The typed reading, or `null` when the buffer is not a tone document. */
  view: ToneView | null;
}

type JsonRecord = Record<string, unknown>;

function isRecord(value: unknown): value is JsonRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function asString(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

const BLANK_CLAUSES: Record<TransitionKind, string> = {
  opening: "",
  bare: "",
  next_topic: "",
};

/**
 * The typed reading of one stored `tone` buffer, or `null`.
 *
 * `null` for anything this cannot present as a tone document — unparseable
 * text, a non-object, a missing required key, or a value whose type the form
 * has no control for. The surface then says so rather than rendering an empty
 * form over content it did not understand and saving that emptiness back.
 *
 * DELIBERATELY STRICTER THAN THE ENGINE, which degrades a wrong-typed clause to
 * "no clause" with a warning (`tracker._tone_str`). A runtime that must not
 * crash mid-conversation is right to be lenient; an EDITOR that showed a blank
 * box over a value it could not read would invite a clinician to save that
 * blank over it.
 */
export function readToneDocument(text: string): ToneView | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return null;
  }
  if (!isRecord(parsed)) {
    return null;
  }

  const intro = parsed.session_intro;
  if (!isRecord(intro) || typeof intro.enabled !== "boolean") {
    return null;
  }
  const greetingTemplate = asString(intro.greeting_template);
  const ask = asString(intro.ask);
  const bgIntro = asString(parsed.bg_intro);
  if (greetingTemplate === null || ask === null || bgIntro === null) {
    return null;
  }

  const template = parsed.domain_change_template;
  let shape: TransitionShape;
  let clauses = BLANK_CLAUSES;
  let single = "";
  if (typeof template === "string") {
    shape = "single";
    single = template;
  } else if (isRecord(template)) {
    shape = "clauses";
    const read: Partial<Record<TransitionKind, string>> = {};
    for (const kind of TRANSITION_KINDS) {
      const clause = asString(template[kind]);
      if (clause === null) {
        return null;
      }
      read[kind] = clause;
    }
    clauses = read as Record<TransitionKind, string>;
  } else {
    return null;
  }

  const banned = parsed.banned_phrases;
  if (!Array.isArray(banned) || banned.some((phrase) => typeof phrase !== "string")) {
    return null;
  }

  return {
    sessionIntro: { enabled: intro.enabled, greetingTemplate, ask },
    bgIntro,
    shape,
    clauses,
    single,
    bannedPhrases: banned as string[],
  };
}

/** Every `tone` document this version carries, in stored order. */
export function toneDocuments(parts: readonly DraftPart[]): ToneDocument[] {
  const documents: ToneDocument[] = [];
  for (const part of parts) {
    const [language, role] = splitPartKey(part.key);
    if (role !== BG_TONE_ROLE || part.kind !== "structured") {
      continue;
    }
    documents.push({
      documentId: part.id,
      language,
      view: readToneDocument(part.text),
    });
  }
  return documents;
}

/**
 * The parts array with `mutate` applied to one tone document's parsed object.
 *
 * THE SINGLE WRITE PATH of this module, for `backgroundDomains.editDocument`'s
 * reasons: it parses, hands the caller the live object, and re-serialises
 * whatever the caller left behind, so no edit can be written in a way that
 * rebuilds the document. An unparseable buffer is returned UNCHANGED rather
 * than replaced — it is content no form was rendered over, and overwriting it
 * would lose a stored version's only readable copy.
 */
function editDocument(
  parts: readonly DraftPart[],
  documentId: string,
  mutate: (document: JsonRecord) => void,
): DraftPart[] {
  return parts.map((part) => {
    if (part.id !== documentId) {
      return part;
    }
    let parsed: unknown;
    try {
      parsed = JSON.parse(part.text);
    } catch {
      return part;
    }
    if (!isRecord(parsed)) {
      return part;
    }
    mutate(parsed);
    return { ...part, text: JSON.stringify(parsed, null, 2) };
  });
}

/**
 * Whether the runtime composes a standalone SELF_INTRO turn.
 *
 * THE TWO TEXTS ARE NOT TOUCHED when this is switched off, and that is the
 * source posture rather than a convenience: MOD stores `enabled: false` beside
 * two empty strings, and a profile that once carried a greeting keeps it so a
 * clinician who switches back finds their words where they left them. What the
 * service refuses is the other direction — an ENABLED introduction with a blank
 * ask, because the turn it composes would ask nothing.
 */
export function setSessionIntroEnabled(
  parts: readonly DraftPart[],
  documentId: string,
  enabled: boolean,
): DraftPart[] {
  return editDocument(parts, documentId, (document) => {
    const intro = document.session_intro;
    if (isRecord(intro)) {
      intro.enabled = enabled;
    }
  });
}

/** One of the session introduction's two texts. */
export function setSessionIntroText(
  parts: readonly DraftPart[],
  documentId: string,
  field: "greeting_template" | "ask",
  value: string,
): DraftPart[] {
  return editDocument(parts, documentId, (document) => {
    const intro = document.session_intro;
    if (isRecord(intro)) {
      intro[field] = value;
    }
  });
}

/** The turn-1 opening the tracker prepends to the first directive. */
export function setBgIntro(
  parts: readonly DraftPart[],
  documentId: string,
  value: string,
): DraftPart[] {
  return editDocument(parts, documentId, (document) => {
    document.bg_intro = value;
  });
}

/** One named transition clause, on a document storing the mapping shape. */
export function setTransitionClause(
  parts: readonly DraftPart[],
  documentId: string,
  kind: TransitionKind,
  value: string,
): DraftPart[] {
  return editDocument(parts, documentId, (document) => {
    const template = document.domain_change_template;
    if (isRecord(template)) {
      template[kind] = value;
    }
  });
}

/** The single shared transition string, on a document storing that shape. */
export function setTransitionSingle(
  parts: readonly DraftPart[],
  documentId: string,
  value: string,
): DraftPart[] {
  return editDocument(parts, documentId, (document) => {
    if (typeof document.domain_change_template === "string") {
      document.domain_change_template = value;
    }
  });
}

/**
 * Switch which shape the transition template is stored in, carrying the text.
 *
 * NON-DESTRUCTIVE IN ONE DIRECTION AND HONEST IN THE OTHER, which is why the
 * caller confirms the lossy one. Collapsing three clauses into one string can
 * only keep one of them, so this keeps the FIRST non-empty in the engine's own
 * order and the surface says so before it happens. Expanding one string into
 * three copies it into all three clauses, which is exactly what the runtime was
 * already doing with it (`_render_domain_change` returns the same string for
 * every kind), so nothing about the interview changes.
 */
export function setTransitionShape(
  parts: readonly DraftPart[],
  documentId: string,
  shape: TransitionShape,
): DraftPart[] {
  return editDocument(parts, documentId, (document) => {
    const template = document.domain_change_template;
    if (shape === "single" && isRecord(template)) {
      const carried =
        TRANSITION_KINDS.map((kind) => asString(template[kind]) ?? "").find(
          (clause) => clause !== "",
        ) ?? "";
      document.domain_change_template = carried;
      return;
    }
    if (shape === "clauses" && typeof template === "string") {
      document.domain_change_template = {
        opening: template,
        bare: template,
        next_topic: template,
      };
    }
  });
}

/**
 * The banned-phrase list, replaced wholesale.
 *
 * ONE SETTER RATHER THAN add/remove/move, because this list is a list of plain
 * strings with no identity of its own: there is no stable id a rule or a
 * tracker resolves against, so an index is the only address and the caller
 * already holds the whole list to render it. A single setter is also what keeps
 * the "replace the value in place" rule true for a value that IS the whole
 * array.
 */
export function setBannedPhrases(
  parts: readonly DraftPart[],
  documentId: string,
  phrases: readonly string[],
): DraftPart[] {
  return editDocument(parts, documentId, (document) => {
    document.banned_phrases = [...phrases];
  });
}

/**
 * The tone document a NEW profile starts from.
 *
 * EVERY REQUIRED KEY, EVERY VALUE EMPTY, and the session introduction off.
 * `library.py` requires all three parts at v1 and `load_variant_tone_config`
 * requires all five keys, so a partial document is not a legal starting point;
 * and an empty tone string is the engine's own "no clause", so this asserts
 * nothing clinical while being a document the runtime can actually serve. It is
 * the structured half of the same honesty `namedParts.UNWRITTEN_PROMPT_TEXT`
 * applies to a required prompt.
 *
 * The MAPPING shape rather than the single string, because that is what both
 * shipped source profiles carry and it is the shape whose three clauses the
 * form can name.
 */
export function blankToneDocument(): string {
  return JSON.stringify(
    {
      session_intro: { enabled: false, greeting_template: "", ask: "" },
      bg_intro: "",
      domain_change_template: { opening: "", bare: "", next_topic: "" },
      banned_phrases: [],
    },
    null,
    2,
  );
}

/**
 * What a catalog row may say about a stored profile version, or `null`.
 *
 * Read DEFENSIVELY off the stored envelope rather than through a draft, on
 * `backgroundDomains.storedDomainSummary`'s rule: a read-only surface must not
 * mint `DraftPart` ids to describe content it is only listing, and a version
 * whose shape this cannot read contributes no facts rather than raising on a
 * catalog render.
 */
export interface BackgroundProfileSummary {
  /** The languages this version carries a complete three-part variant at. */
  completeLanguages: string[];
  /** `null` when no `tone` document could be read. */
  sessionIntroEnabled: boolean | null;
  bannedPhraseCount: number | null;
  transitionShape: TransitionShape | null;
}

export function storedProfileSummary(
  content: unknown,
): BackgroundProfileSummary | null {
  if (!isRecord(content) || !Array.isArray(content.parts)) {
    return null;
  }
  const required = new Set([
    BG_TONE_ROLE,
    BG_PERSONALITY_ROLE,
    BG_CONVERSATION_PROMPT_ROLE,
  ]);
  const rolesByLanguage = new Map<string, Set<string>>();
  let tone: ToneView | null = null;

  for (const part of content.parts) {
    if (!isRecord(part) || typeof part.key !== "string") {
      continue;
    }
    const [language, role] = splitPartKey(part.key);
    if (language !== null && required.has(role)) {
      const held = rolesByLanguage.get(language) ?? new Set<string>();
      held.add(role);
      rolesByLanguage.set(language, held);
    }
    if (role === BG_TONE_ROLE && tone === null && isRecord(part.data)) {
      tone = readToneDocument(JSON.stringify(part.data));
    }
  }

  const completeLanguages = [...rolesByLanguage.entries()]
    .filter(([, held]) => [...required].every((role) => held.has(role)))
    .map(([language]) => language)
    .sort();

  return {
    completeLanguages,
    sessionIntroEnabled: tone === null ? null : tone.sessionIntro.enabled,
    bannedPhraseCount: tone === null ? null : tone.bannedPhrases.length,
    transitionShape: tone === null ? null : tone.shape,
  };
}
